<template>
  <ProductDetailsModal
    v-if="modalVisible"
    :agencyId="modalProduct.AgencyId"
    :doNotDisplayUkdId="modalProduct.DoNotDisplayUkdId"
    @closeModal="modalVisible = false"
  />
  <div class="products-table content-box" data-cy="products-table">
    <div
      class="products-table-header grid-control__container table-modifiers clearfix"
    >
      <TableSearch
        class="product-search"
        :value="searchInput"
        placeholder="Search by Name or Company or Brand or UKD ID"
        @searchSubmitted="onSearchSubmitted"
        data-cy="products-table-search"
      />
      <GridActionsMenu
        class="pull-right"
        @actionClicked="onActionClicked"
        data-cy="grid-actions"
      />
    </div>
    <ProductsGrid
      :products="products"
      :productsLoading="productsLoading"
      :pageSize="productsTake"
      :skip="productsSkip"
      :totalProducts="totalProducts"
      :filter="productsFilter"
      :sort="sortDescriptors"
      ref="gridRef"
      :getPropValues="getProductPropValuesAsync"
      @productDetailsClicked="onProductDetailsClicked"
      @sortChanged="onSortChanged"
      @itemsChanged="onProductsChanged"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler :filter="productsFilter" ref="menuIconHandlerRef" />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import ProductsGrid from "./ProductsGrid.vue";
import type { OuKashUnionUkdProducts } from "@/Domain/OuKashUnionUkdProducts";
import ProductDetailsModal from "./ProductDetailsModal.vue";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";

import type { GetMatchingProductsOnUkdOnSingleSearchFieldWithFilteringRequest } from "@/api/ukdProducts";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { CacheService } from "@/shared/services/CacheService";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import {
  FilterValuesTake,
  getJsonFilterString,
} from "@/shared/helpers/filtersHelper";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import GridActionsMenu from "@/shared/ui/inputs/GridActionsMenu.vue";
import { setQueryParameter } from "@/shared/helpers/urlHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const main = useMainStore();

const defaultTake = 25;

const searchInput = ref<string>(main.UkdProductFilterCriteria);
const products = ref<OuKashUnionUkdProducts[]>([]);
const totalProducts = ref<number>(0);
const modalVisible = ref<boolean>(false);
const productsLoading = ref<boolean>(false);
const modalProduct = ref<OuKashUnionUkdProducts>({} as OuKashUnionUkdProducts);
const productsFilter = ref<CompositeFilterDescriptor>({
  logic: "and",
  filters: [{} as FilterDescriptor],
});
const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);
const productsTake = ref<number>(defaultTake);
const productsSkip = ref<number>(0);
const clearGrid = ref<boolean>(false);

const gridRef = ref<InstanceType<typeof ProductsGrid> | null>(null);
const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);

const tableName = "UkdProducts";

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const getMatchingProductsAsync = async function (
  searchString: string,
  skip: number,
  take: number,
  filter: CompositeFilterDescriptor | null = null,
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: searchString,
    caller: main.userLogin,
    filters: getJsonFilterString(filter),
    skip: skip,
    take: take,
    includeConfidentials: false,
    sortField: sortField.value,
    ascending: sortAscending.value,
    valuesForFieldOnly: valuesForFieldOnly,
  } as GetMatchingProductsOnUkdOnSingleSearchFieldWithFilteringRequest;

  const endpoint = "GetMatchingProductsOnUkdOnSingleSearchFieldWithFiltering";

  if (!valuesForFieldOnly) return api.get(endpoint, requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, endpoint),
    () => api.get(endpoint, requestParams)
  );
};

const clearFilters = function () {
  searchInput.value = "";
  productsFilter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };
  sortField.value = "";
  sortAscending.value = false;

  // No product should be shown
  // unless a new search is performed
  clearGrid.value = true;
  products.value = [];
  totalProducts.value = 0;
};

const onProductDetailsClicked = function (product: OuKashUnionUkdProducts) {
  modalProduct.value = product;
  modalVisible.value = true;
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  clearGrid.value = false;
  productsSkip.value = 0;
  searchMatchingProductsAsync(productsFilter.value);
};

const onProductsChanged = function (options: ItemChangedOptions) {
  if (options.filterEvent) {
    productsFilter.value.filters = options.filterEvent.filter.filters;
  }
  if (productsTake.value != options.take) {
    productsTake.value = options.take;
    GridOptionsService.savePageSizeAsync(tableName, options.take);
  }
  productsSkip.value = options.skip;
  searchMatchingProductsAsync(productsFilter.value);
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  productsSkip.value = 0;
  searchMatchingProductsAsync(productsFilter.value);
};

const searchMatchingProductsAsync = async function (
  filter: CompositeFilterDescriptor | null = null
) {
  if (productsLoading.value || clearGrid.value) return;

  productsLoading.value = true;

  await handleResponsePromiseAsync(
    getMatchingProductsAsync(
      searchInput.value,
      productsSkip.value,
      productsTake.value,
      filter
    ),
    (data) => {
      products.value = data.ListItems;
      totalProducts.value = data.TotalItemsInList;
    },
    "ProductsTable.vue",
    "SearchMatchingProducts"
  );

  productsLoading.value = false;
};

const getProductPropValuesAsync = async function (
  column: GridColumnPropsExtended
) {
  let result: OuKashUnionUkdProducts[] = [];
  await handleResponsePromiseAsync(
    getMatchingProductsAsync(
      searchInput.value,
      0,
      999999,
      productsFilter.value,
      column.field
    ),
    (data) => {
      result = data.ListItems;
    },
    "ProductsTable.vue",
    "getProductPropValuesAsync()"
  );
  return result;
};

const resetPagination = function () {
  if (productsTake.value === defaultTake) return;

  productsSkip.value = 0;
  productsTake.value = defaultTake;
  GridOptionsService.clearPageSizeAsync(tableName);
  searchMatchingProductsAsync();
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

const onActionClicked = function (actionType: GridMenuActionType) {
  switch (actionType) {
    case GridMenuActionType.ClearAllFilters:
      clearFilters();
      break;
    case GridMenuActionType.ResetPagination:
      resetPagination();
      break;
    case GridMenuActionType.ResetColumnOrder:
      updateColumnMenuIcons();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetPagination();
      updateColumnMenuIcons();
      break;
  }
  if (gridRef.value) gridRef.value.executeAction(actionType);
};

watchEffect(() => {
  console.debug(
    "ProductsTable.vue, searchInput watch updated:",
    searchInput.value
  );
  main.UkdProductFilterCriteria = searchInput.value;
  setQueryParameter("search", searchInput.value);
});

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) productsTake.value = pageSize;
  if (searchInput.value) await searchMatchingProductsAsync();
});
</script>

<style>
.product-search {
  width: 400px;
}
</style>
